<template>
  <MainLayout
    :initialDrawerExpanded="false"
    contentClass="pa-md-4"
    :has-sidebar="false"
  >
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">

        <div v-if="loading" class="pa-8 d-flex justify-center">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="engine">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2">mdi-robot-outline</v-icon>
                <h2>{{ engine.name }}</h2>
                <v-spacer />
                <v-btn
                  v-if="isSuperuser"
                  small text
                  :loading="cloning"
                  @click="cloneEngine"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  <span class="text-none">Dupliquer</span>
                </v-btn>
                <v-btn
                  v-if="isSuperuser"
                  small text color="error"
                  class="ml-1"
                  @click="deleteDialog = true"
                >
                  <v-icon left small>mdi-delete-outline</v-icon>
                  <span class="text-none">Supprimer</span>
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row align="start">
            <!-- Left: prompt -->
            <v-col cols="12" lg="7">
              <v-card elevation="0" class="mb-4">
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-text-box-outline</v-icon>
                  Prompt système
                </v-card-title>
                <v-card-text>
                  <PromptEditor
                    v-model="promptText"
                    :disabled="!engine.active_prompt"
                    :hint="engine.active_prompt
                      ? `Version active : v${engine.active_prompt.version} — une nouvelle version sera créée automatiquement à la sauvegarde.`
                      : 'Aucun prompt actif — assignez d\'abord un prompt depuis l\'administration.'"
                    :versions="promptVersions"
                    :active-version="engine.active_prompt ? engine.active_prompt.version : null"
                  >
                    <template #actions>
                      <v-btn
                        small
                        color="primary"
                        elevation="0"
                        :loading="saving"
                        :disabled="!isDirty"
                        class="align-self-center mr-2"
                        @click="save"
                      >
                        <v-icon left small>mdi-content-save-outline</v-icon>
                        <span class="text-none">Enregistrer</span>
                      </v-btn>
                    </template>
                  </PromptEditor>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Right: settings + base documentaire + sync -->
            <v-col cols="12" lg="5">
              <v-card elevation="0" class="mb-4">
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-cog-outline</v-icon>
                  Paramètres
                </v-card-title>
                <v-card-text>
                  <v-switch
                    v-model="engine.is_active"
                    label="Actif"
                    dense
                    persistent-hint
                    hint="Ce moteur est proposé aux utilisateurs. Désactivez-le pour le masquer sans le supprimer."
                    class="mt-0"
                    :loading="togglingActive"
                    @change="toggleActive"
                  />
                  <v-switch
                    v-if="isSuperuser"
                    v-model="engine.is_demo"
                    label="Démo"
                    dense
                    persistent-hint
                    hint="Réservé aux comptes de démonstration. Les utilisateurs standards ne voient pas ce moteur."
                    class="mt-4"
                    :loading="togglingDemo"
                    @change="toggleDemo"
                  />
                </v-card-text>
              </v-card>

              <v-card elevation="0" class="mb-4">
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-google-drive</v-icon>
                  Base documentaire
                </v-card-title>
                <v-card-text>
                  <v-select
                    v-model="selectedFolderMappingId"
                    :items="folderMappings"
                    item-text="name"
                    item-value="id"
                    label="Dossier / Vector Store"
                    outlined
                    clearable
                    hint="Sélectionnez la base documentaire associée à ce moteur."
                    persistent-hint
                  />
                </v-card-text>
              </v-card>

              <v-card v-if="engine.folder_mapping" elevation="0" class="mb-4">
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-sync</v-icon>
                  Synchronisation
                  <v-spacer />
                  <v-btn small elevation="0" :loading="syncLoading" @click="loadPreview">
                    <v-icon left small>mdi-eye-outline</v-icon>
                    <span class="text-none">Prévisualiser</span>
                  </v-btn>
                </v-card-title>
                <v-card-text class="pa-0 pt-2">
                  <sync-mapping-card
                    v-if="mappingPreview"
                    :mapping="mappingPreview"
                    :running="syncRunning"
                    :previewing="previewingMappingId === mappingPreview.mapping_id"
                    :syncing="syncingMappingId === mappingPreview.mapping_id"
                    @preview="loadPreviewForMapping"
                    @sync="syncMapping"
                  />
                  <div v-else class="caption grey--text px-4 pb-4">
                    Cliquez sur « Prévisualiser » pour voir l'état de synchronisation.
                  </div>
                </v-card-text>
              </v-card>

            </v-col>
          </v-row>

          <!-- Delete confirmation dialog -->
          <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
              <v-card-title class="subtitle-1 font-weight-medium">
                Supprimer ce moteur ?
              </v-card-title>
              <v-card-text>
                Cette action est irréversible. Le moteur « {{ engine.name }} » sera définitivement supprimé.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="deleteDialog = false">Annuler</v-btn>
                <v-btn color="error" text :loading="deleting" @click="deleteEngine">Supprimer</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Sync progress dialog -->
          <v-dialog v-model="progressDialog" max-width="560" scrollable>
            <v-card>
              <v-card-title class="subtitle-1 font-weight-medium">
                <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                Progression
                <v-spacer />
                <v-btn icon small @click="progressDialog = false">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text style="max-height: 400px; overflow-y: auto;">
                <div
                  v-for="(event, i) in syncLog"
                  :key="i"
                  class="d-flex align-center py-1"
                >
                  <v-icon small class="mr-2" :color="logColor(event.type)">
                    {{ logIcon(event.type) }}
                  </v-icon>
                  <span class="caption">{{ logLabel(event) }}</span>
                </div>
                <v-alert
                  v-if="!syncRunning && syncDone"
                  :type="hasLogErrors ? 'warning' : 'success'"
                  dense outlined class="mt-3 mb-0"
                >
                  {{ hasLogErrors ? 'Synchronisation terminée avec des erreurs.' : 'Synchronisation terminée avec succès.' }}
                </v-alert>
              </v-card-text>
              <v-card-actions v-if="!syncRunning && syncDone">
                <v-spacer />
                <v-btn text @click="progressDialog = false">Fermer</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>

        <v-row v-else>
          <v-col cols="12" md="8" offset-md="2">
            <v-alert type="error" outlined>
              Moteur introuvable.
            </v-alert>
          </v-col>
        </v-row>

      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import PromptEditor from '@/components/PromptEditor.vue'
import SyncMappingCard from '@/components/sync/SyncMappingCard.vue'
import {
  fetchAdminEngines,
  fetchAdminFolderMappings,
  fetchAdminPrompt,
  createAdminEngine,
  updateAdminEngine,
  deleteAdminEngine,
  updateAdminPrompt,
} from '@/services/adminApi'

export default {
  name: 'AdminEngineEditView',
  components: { PageBack, MainLayout, PromptEditor, SyncMappingCard },

  data() {
    return {
      loading: true,
      saving: false,
      engine: null,
      promptText: '',
      originalPromptText: '',
      selectedFolderMappingId: null,
      originalFolderMappingId: null,
      folderMappings: [],
      promptVersions: [],
      previewingMappingId: null,
      syncingMappingId: null,
      progressDialog: false,
      deleteDialog: false,
      deleting: false,
      cloning: false,
      togglingActive: false,
      togglingDemo: false,
    }
  },

  computed: {
    isSuperuser() {
      return this.$store.getters['settings/isSuperuser']
    },

    engineId() {
      return this.$route.params.id
    },

    isDirty() {
      return (
        this.promptText !== this.originalPromptText ||
        this.selectedFolderMappingId !== this.originalFolderMappingId
      )
    },

    syncLoading()  { return this.$store.state.sync.loading },
    syncRunning()  { return this.$store.state.sync.running },
    syncLog()      { return this.$store.state.sync.log },
    syncDone()     { return this.syncLog.some(e => e.type === 'all_done') },
    hasLogErrors() { return this.syncLog.some(e => e.type.endsWith('_error') || e.type === 'error') },

    mappingPreview() {
      const preview = this.$store.state.sync.preview
      if (!preview || !this.engine?.folder_mapping) return null
      return preview.find(m => m.mapping_id === this.engine.folder_mapping.id) || null
    },
  },

  async mounted() {
    try {
      const [engines, folderMappings] = await Promise.all([
        fetchAdminEngines(),
        fetchAdminFolderMappings(),
      ])
      this.folderMappings = folderMappings
      this.engine = engines.find(e => e.id === this.engineId) || null
      if (this.engine) {
        this.promptText = this.engine.active_prompt ? this.engine.active_prompt.text : ''
        this.originalPromptText = this.promptText
        this.selectedFolderMappingId = this.engine.folder_mapping ? this.engine.folder_mapping.id : null
        this.originalFolderMappingId = this.selectedFolderMappingId
        if (this.engine.active_prompt) {
          const prompt = await fetchAdminPrompt(this.engine.active_prompt.id)
          this.promptVersions = prompt.versions || []
        }
      }
    } catch {
      this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement.' })
    } finally {
      this.loading = false
    }
  },

  methods: {
    loadPreview() {
      const id = this.engine?.folder_mapping?.id
      this.$store.dispatch('sync/fetchPreview', id ? [id] : null)
    },

    async loadPreviewForMapping(id) {
      this.previewingMappingId = id
      await this.$store.dispatch('sync/fetchPreview', [id])
      this.previewingMappingId = null
    },

    async syncMapping(id) {
      this.syncingMappingId = id
      this.progressDialog = true
      await this.$store.dispatch('sync/runSync', [id])
      this.syncingMappingId = null
    },

    logIcon(type) {
      const icons = {
        mapping_start: 'mdi-folder-outline',
        upload_start:  'mdi-upload',
        upload_done:   'mdi-check',
        upload_error:  'mdi-alert-circle-outline',
        remove_start:  'mdi-delete-outline',
        remove_done:   'mdi-check',
        remove_error:  'mdi-alert-circle-outline',
        mapping_done:  'mdi-check-circle-outline',
        all_done:      'mdi-check-all',
        error:         'mdi-alert-circle-outline',
      }
      return icons[type] || 'mdi-circle-small'
    },
    logColor(type) {
      if (type.endsWith('_error') || type === 'error') return 'error'
      if (type.endsWith('_done') || type === 'all_done') return 'success'
      if (type === 'remove_start') return 'warning'
      return 'primary'
    },
    logLabel(event) {
      const { type, mapping, file, message } = event
      if (type === 'mapping_start') return `${mapping} — démarrage`
      if (type === 'mapping_done')  return `${mapping} — synchronisé`
      if (type === 'upload_start')  return `${mapping} / ${file} — envoi…`
      if (type === 'upload_done')   return `${mapping} / ${file} — ajouté`
      if (type === 'upload_error')  return `${mapping} / ${file} — erreur : ${message}`
      if (type === 'remove_start')  return `${mapping} / ${file} — suppression…`
      if (type === 'remove_done')   return `${mapping} / ${file} — supprimé`
      if (type === 'remove_error')  return `${mapping} / ${file} — erreur : ${message}`
      if (type === 'all_done')      return 'Synchronisation terminée'
      if (type === 'error')         return `${mapping} — erreur : ${message}`
      return type
    },

    async toggleActive() {
      this.togglingActive = true
      try {
        await updateAdminEngine(this.engineId, { is_active: this.engine.is_active })
      } catch {
        this.engine.is_active = !this.engine.is_active
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la mise à jour.' })
      } finally {
        this.togglingActive = false
      }
    },

    async toggleDemo() {
      this.togglingDemo = true
      try {
        await updateAdminEngine(this.engineId, { is_demo: this.engine.is_demo })
      } catch {
        this.engine.is_demo = !this.engine.is_demo
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la mise à jour.' })
      } finally {
        this.togglingDemo = false
      }
    },

    async cloneEngine() {
      this.cloning = true
      try {
        const payload = {
          name: `Copie de ${this.engine.name}`,
          provider_slug: this.engine.provider_slug,
          model: this.engine.model,
          is_active: false,
          is_demo: this.engine.is_demo,
        }
        if (this.engine.active_prompt) payload.active_prompt_id = this.engine.active_prompt.id
        if (this.engine.folder_mapping) payload.folder_mapping_id = this.engine.folder_mapping.id
        const created = await createAdminEngine(payload)
        this.$router.push({ name: 'admin_engine_edit', params: { id: created.id } })
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la duplication.' })
      } finally {
        this.cloning = false
      }
    },

    async deleteEngine() {
      this.deleting = true
      try {
        await deleteAdminEngine(this.engineId)
        this.$router.push({ name: 'admin' })
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la suppression.' })
        this.deleting = false
        this.deleteDialog = false
      }
    },

    async save() {
      this.saving = true
      try {
        const enginePatch = {}

        if (this.promptText !== this.originalPromptText && this.engine.active_prompt) {
          await updateAdminPrompt(this.engine.active_prompt.id, { new_text: this.promptText })
          enginePatch.active_prompt_id = this.engine.active_prompt.id
        }

        if (this.selectedFolderMappingId !== this.originalFolderMappingId) {
          enginePatch.folder_mapping_id = this.selectedFolderMappingId
        }

        if (Object.keys(enginePatch).length) {
          const updated = await updateAdminEngine(this.engineId, enginePatch)
          this.engine = updated
          this.promptText = updated.active_prompt ? updated.active_prompt.text : ''
          this.originalPromptText = this.promptText
          this.selectedFolderMappingId = updated.folder_mapping ? updated.folder_mapping.id : null
          this.originalFolderMappingId = this.selectedFolderMappingId
          if (updated.active_prompt) {
            const prompt = await fetchAdminPrompt(updated.active_prompt.id)
            this.promptVersions = prompt.versions || []
          }
        }

        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'success', message: 'Modifications enregistrées.' })
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la sauvegarde.' })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
